import type { IStationsLineRef } from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import { HintTooltip } from '../../HintTooltip';

/**
 * 「運行情報と連携」(アイステイションズ路線) の複数選択 UI。
 *
 * 接続線遅延コンテンツの part1 / part2 と、運行情報 (`train_info`) の **すべてから** 使う共通部品。
 * - part1 (`connecting_line_delay`): `properties.istations_lines`
 *   (`ConnectingLineDelayFields.tsx` から利用)
 * - part2 (`connecting_line_delay_by_station`): `properties.lines[].istations_lines`
 *   (`ConnectingLineBlock.tsx` から利用)
 * - 運行情報 (`train_info`): `properties.lines[].istations_lines`
 *   (`TrainInfoFields.tsx` から利用)
 *
 * 【設計上の重要な制約: ATOS の route_id と連動させない】
 *
 * 本コンポーネントは **意図的に ATOS 線区マスタ (`RouteMaster` / `routes`) を参照しない**。
 * 接続線遅延の判定対象は ATOS の線名コードだが、運行情報の参照元として紐付ける
 * アイステイションズ路線は別系統のコードであり、「ATOS の route_id と一致しない
 * 路線を運用側で自由に選べること」が要件である。
 * props に `routes` を受け取らない設計にすることで、将来的にうっかり連動ロジック
 * (ATOS 選択に応じた絞り込み等) が混入することを構造的に防いでいる。
 *
 * 選択肢は `fetchIStationsLines()` (`istations-lines.json`) 由来のマスタのみで構成する。
 *
 * 【表示方針】
 * - 見出しは既定で「運行情報と連携」。常時表示する説明は 1 行だけにし、詳しい説明は
 *   見出し横の ⓘ (`HintTooltip`) に集約する。選択済みの路線はチップ (名称 + 小さなコード + ×)
 *   で並べ、追加プルダウンを一覧のすぐ下に置く。
 * - 「接続線遅延とは独立している」旨は、どの画面でも ⓘ の中に必ず書く。
 * - `required` が true (運行情報カテゴリ) のときは 0 件を警告として見せる。
 *   false (接続線遅延) のときは「未設定でも保存できます」と案内する。
 *   これらは状態を伝える文なので ⓘ には隠さない (`HintTooltip` の使い分けに従う)。
 * - `description` を渡すと、ⓘ の中の先頭の説明をそれに置き換える
 *   (運行情報は「配信中の運行情報をそのまま読み上げる」旨の専用文言を渡す)。
 */
export function IStationsLinesSelect({
  testIdPrefix,
  ariaLabelPrefix,
  options,
  value,
  maxItems,
  required = false,
  title = '運行情報と連携',
  description,
  onChange,
}: {
  /**
   * data-testid の接頭辞。part1 / part2 で同一画面に複数出ても一意になるよう
   * 呼び出し側で異なる値を渡す (例: `connecting-istations-lines` /
   * `connecting-line-istations-lines-0`)。
   */
  testIdPrefix: string;
  /**
   * aria-label の接頭辞。E2E / スクリーンリーダー向けに、どの接続線に属する
   * 選択欄かを区別できる文言を渡す (例: `接続線#1 `)。
   */
  ariaLabelPrefix?: string;
  /** 選択肢 (アイステイションズ路線マスタ)。取得失敗時は空配列でよい。 */
  options: readonly IStationsLine[];
  /** 現在選択されているアイステイションズ路線 (順序は入力順)。 */
  value: readonly IStationsLineRef[];
  /**
   * 選択できる上限件数。既定 10 件で、サーバ側 `MAX_ISTATIONS_LINES` と一致させる
   * (上限に達したら追加プルダウンを disabled にして 400 を事前に防ぐ)。
   */
  maxItems?: number;
  /**
   * 1 件以上の選択が必須か。運行情報 (`train_info`) は路線が無いと放送内容が空になり
   * 保存時にバリデーションエラーになるため true を渡す。接続線遅延は任意なので既定 false。
   */
  required?: boolean;
  /** 見出し。既定は「運行情報と連携」。 */
  title?: string;
  /**
   * ⓘ の中の先頭に出す説明。未指定なら接続線遅延向けの既定文
   * (「接続線遅延とは独立した設定で、ATOS の線区と一致しない路線も自由に選べます。」) を出す。
   */
  description?: React.ReactNode;
  onChange: (next: IStationsLineRef[]) => void;
}): React.JSX.Element {
  const limit = maxItems ?? 10;
  const selected = Array.isArray(value) ? value : [];
  const isFull = selected.length >= limit;

  // すでに選択済みの route_id は追加プルダウンから除外する
  // (重複はサーバ側で 400 になるため、UI 側でも選べないようにする)。
  const selectedIds = new Set(selected.map((l) => l.route_id));
  const addableOptions = options.filter((o) => !selectedIds.has(o.route_id));

  /** route_id を追加する。マスタから route_name をスナップショットとして持たせる。 */
  const addLine = (routeId: string): void => {
    if (!routeId || selectedIds.has(routeId) || isFull) return;
    const master = options.find((o) => o.route_id === routeId);
    const entry: IStationsLineRef = master?.route_name
      ? { route_id: routeId, route_name: master.route_name }
      : { route_id: routeId };
    onChange([...selected, entry]);
  };

  /** index 番目の選択を取り除く。 */
  const removeAt = (index: number): void => {
    const next = selected.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div className="istations-lines-select connecting-section" data-testid={testIdPrefix}>
      <div className="istations-lines-select__header">
        <div className="connecting-section__title">
          {title}
          <HintTooltip
            label={`${ariaLabelPrefix ?? ''}${title}の説明`}
            testId={`${testIdPrefix}-hint`}
          >
            {description !== undefined ? (
              description
            ) : (
              <>接続線遅延とは独立した設定で、ATOS の線区と一致しない路線も自由に選べます。</>
            )}
            <br />
            路線は最大 {limit} 件まで選べます。配信のある全路線の文面がこの並び順に読み上げられます
            （同じ文面が複数路線に配信されているときは 1 回だけ）。
            選びすぎると放送が長くなるため、必要な路線に絞ってください。
          </HintTooltip>
        </div>
        <span
          className="istations-lines-select__count"
          data-testid={`${testIdPrefix}-count`}
          aria-label={`選択中 ${selected.length} 件 / 上限 ${limit} 件`}
        >
          {selected.length} / {limit} 件
        </span>
      </div>
      <p className="connecting-section__lead">運行情報を取得する路線を選びます。</p>

      {/*
        選択済み一覧。0 件のときは空メッセージを出す。
        必須 (運行情報) なら警告、任意 (接続線遅延) なら「未設定でも保存できます」。
      */}
      {selected.length === 0 ? (
        <div
          className={`connecting-section__empty${required ? ' is-required' : ''}`}
          data-testid={`${testIdPrefix}-empty`}
        >
          {required
            ? '路線が未選択です。下のプルダウンから 1 つ以上選んでください。'
            : '未設定です。下のプルダウンから追加してください（未設定でも保存できます）。'}
        </div>
      ) : (
        <ul className="istations-chip-list">
          {selected.map((line, i) => {
            // 表示名はマスタの最新名称 → 保存済みスナップショット → route_id の順で解決する。
            // マスタから削除された路線でも既存の設定値が画面から消えないようにする。
            const master = options.find((o) => o.route_id === line.route_id);
            const displayName = master?.route_name || line.route_name || line.route_id;
            const isUnknown = master === undefined;
            return (
              <li key={`${line.route_id}-${i}`} data-testid={`${testIdPrefix}-item-${i}`}>
                <span className="istations-chip-list__name">
                  {displayName}
                  <code>{line.route_id}</code>
                  {isUnknown ? (
                    <span className="istations-chip-list__warn">(一覧にない路線)</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="istations-chip-list__remove"
                  aria-label={`${ariaLabelPrefix ?? ''}アイステイションズ路線 ${displayName} を削除`}
                  title="この路線を外す"
                  data-testid={`${testIdPrefix}-remove-${i}`}
                  onClick={() => removeAt(i)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        追加用プルダウン。選択したら即 onChange して value を空に戻す
        (「追加操作」専用のセレクトなので選択状態は保持しない)。
      */}
      <select
        aria-label={`${ariaLabelPrefix ?? ''}アイステイションズ路線を追加`}
        data-testid={`${testIdPrefix}-add-select`}
        value=""
        disabled={isFull || addableOptions.length === 0}
        onChange={(e) => addLine(e.target.value)}
      >
        <option value="">
          {isFull
            ? `(上限 ${limit} 件に達しています)`
            : addableOptions.length === 0
              ? '(追加できる路線がありません)'
              : '＋ 路線を追加…'}
        </option>
        {addableOptions.map((o) => (
          <option key={o.route_id} value={o.route_id}>
            {o.route_name}（{o.route_id}）
          </option>
        ))}
      </select>
    </div>
  );
}
